import React, { ReactElement, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { api } from '../common/api'
import { selectedSiteSelector } from '../authentication/redux'
import { DEFAULT_ANNOUNCEMENT, getAutocompleteMainNumberCandidates, getAutocompleteSiteAnnouncementsWithDefaultSelection } from '../common/util/helpers'
import { ErrorOption } from 'react-hook-form/dist/types/errors'
import { useFormSubmission } from '../common/servercall/useFormSubmission'
import FormContainer from '../common/form/FormContainer'
import FormGroup from '../common/form/FormGroup'
import FormRow from '../common/form/FormRow'
import FormFieldBox from '../common/form/FormFieldBox'
import TextInput from '../common/form/TextInput'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { SiteGeneral } from '../app/types'

const schema = yup.object({
    alias: yup.string().trim().max(16, 'too-long'),
    mainNumber: yup.string().nullable(),
    musicOnHoldFileName: yup.string().nullable(),
})

type props = {
    general: SiteGeneral
    buildFormButtons: (isSubmitting: boolean) => ReactElement
    onSuccess: () => void
}

export const GeneralForm = ({ general, onSuccess, buildFormButtons }: props) => {
    const site = useSelector(selectedSiteSelector)
    const { t } = useTranslation()

    const { control, handleSubmit, setError } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            alias: general.alias ?? '',
            mainNumber: general.mainNumber ?? '',
            musicOnHoldFileName: general.musicOnHoldFileName ?? DEFAULT_ANNOUNCEMENT,
        },
    })

    const onSubmit = useCallback(
        async (formData: SiteGeneral) => {
            if (!site) return
            await api.patch(`/api/site/${site.productId}`, { ...formData, musicOnHoldFileName: formData.musicOnHoldFileName || DEFAULT_ANNOUNCEMENT })
            onSuccess()
        },
        [site, onSuccess],
    )

    const checkError = useCallback(
        (name: any, error: ErrorOption) => {
            setError(name, error)
        },
        [setError],
    )

    const { submit, isSubmitting } = useFormSubmission<SiteGeneral>(onSubmit, checkError)

    const mainNumberOptionParams = useMemo(() => getAutocompleteMainNumberCandidates(t, site, general.mainNumber), [site, t, general])
    const musicOnHoldOptionParams = useMemo(
        () => getAutocompleteSiteAnnouncementsWithDefaultSelection(t, 5000, site, general.musicOnHoldFileName ?? DEFAULT_ANNOUNCEMENT),
        [site, t, general],
    )

    return (
        <FormContainer onSubmit={handleSubmit(submit)}>
            <FormGroup fullWidth={true}>
                <FormRow>
                    <FormFieldBox>
                        <TextInput readonly={false} label={t('site.cards.general.alias')} name={'alias'} control={control} required={false} />
                    </FormFieldBox>
                    <FormFieldBox>
                        <TextInput readonly={false} label={t('site.cards.general.main-number')} name={'mainNumber'}
                                   control={control} required={false} autoCompleteOptions={mainNumberOptionParams} />
                    </FormFieldBox>
                </FormRow>
                <FormRow>
                    <FormFieldBox>
                        <TextInput readonly={false} label={t('site.cards.general.music-on-hold')} name={'musicOnHoldFileName'}
                                   control={control} required={false} autoCompleteOptions={musicOnHoldOptionParams} />
                    </FormFieldBox>
                </FormRow>
            </FormGroup>
            {buildFormButtons(isSubmitting)}
        </FormContainer>
    )
}
